// src/Components/ReleaseAssets.js
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { api } from '../api';
import { ReactComponent as InfoIcon } from '../assets/icons/info.svg';
import { ReactComponent as ViewIcon } from '../assets/icons/view.svg';
import { ReactComponent as DownloadIcon } from '../assets/icons/download.svg';

const InfoHelper = ({ text }) => {
  const [open, setOpen] = useState(false);


  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-label="Show help"
        style={{
          // reset button styles
          background: 'transparent',
          border: 'none',
          padding: 0,
          marginLeft: 8,            // space from the label
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <InfoIcon style={{ width: 14, height: 14, fill: '#aaa' }} />
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '100%',           // right-aligned to the label area
            transform: 'translateY(-50%)',
            marginLeft: 8,
            width: 350,             // wide so text isn't squished
            background: '#222',
            color: '#fff',
            padding: '10px',
            borderRadius: 6,
            fontSize: 13,
            lineHeight: 1.4,
            whiteSpace: 'pre-line',
            zIndex: 20,
            boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
          }}
        >
          {text}
        </div>
      )}
    </div>
  );
};



const UPLOAD_HELPERS = {
  losslessMaster:
    'ONLY: WAV, 16bit, 44.1 kHz\nPlease format the file: Artist Name - Song Name (ft. ?) [Master]',
  mp3Master:
    'Specs: MP3, 320 kbps\nPlease format the file: Artist Name - Song Name (ft. ?) [Master]',
  losslessInstrumental:
    'ONLY: WAV, 16bit, 44.1 kHz\n[Skip if there are no vocals on the original]\nPlease format the file: Artist Name - Song Name (ft. ?) [Instrumental]',
  losslessCleanMaster:
    'ONLY: WAV, 16bit, 44.1 kHz\n[Skip if the original has no explicit lyrics. If it has explicit language, include a clean version for Synch + Radio]\nPlease format the file: Artist Name - Song Name (ft. ?) [Clean]',
  acapella:
    'If the original has vocals, please include the acapella for synch and remix opportunities.\nPlease format the file: Artist Name - Song Name (ft. ?) [Acapella]',
  artwork:
    '[3000 x 3000 px, JPEG, 300 DPI]\nIf you haven’t created artwork yet or will create it with our team, you can skip this. If you have a draft, please upload it here.',
  artworkPsd:
    'Upload the layered artwork source file (PSD) if available.',
    pressPics:
    'Please upload a ZIP file of press pictures.',
};

// PUT to R2 with progress (use XHR because fetch has no upload progress)
function putWithProgress(url, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType || 'application/octet-stream');

    if (xhr.upload && typeof onProgress === 'function') {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(file);
  });
}

export default function ReleaseAssets({
  Row,
  Chip,
  input,
  state,
  allArtists = [],
  setField,
  onWriterChange,
  addWriter,
  removeWriter,
  setPendingFile,
  doUpload,
  onSave,          // per-release save handler
  projectType,
  onViewFile,
  onDownloadFile,
  releaseId,
  guest = false,  
  // NEW props (from AssetCollection)
  locked = false,  // when true, form is locked/read-only
  canSubmit = false, // all required fields satisfied
  submitting = false, // submit in-flight
  onSubmit,       // handler to call POST /submit
  autoUploadFile,
}) {

    // --- Save & Submit UI state ---
  const [localSubmitting, setLocalSubmitting] = useState(false);
 


  // Always: Save first, then attempt submit (server will validate and return missing)
  const handleSaveAndSubmit = async () => {
  if (locked || !onSave || !onSubmit) return;
  try {
    setLocalSubmitting(true);
    await onSave();
    await onSubmit();
  } finally {
    setLocalSubmitting(false);
  }
};
  const st = state || {};

  const showTracklist  = projectType === 'Album' || projectType === 'EP';
  const mainArtists    = Array.isArray(st.mainArtists)  ? st.mainArtists  : [];
  const contributors   = Array.isArray(st.contributors) ? st.contributors : [];
  const writers        = Array.isArray(st.writers)      ? st.writers      : [];
  const [uploading, setUploading] = useState({});  // { label: boolean }
  const [progress, setProgress] = useState({});    // { label: number 0–100 }
  const [replacing, setReplacing] = useState({});  // { label: boolean }
  const [justReplaced, setJustReplaced] = useState({}); // { [label]: true } optimistic flag
  // is *any* label currently uploading for this release?
const anyUploading = useMemo(
  () => Object.values(uploading).some(Boolean),
  [uploading]
);
// Auto-upload as soon as a file is chosen – pass the File directly to the parent uploader
const autoUpload = async (key, file) => {
  if (!file || locked) return;

  // ⛔ block if another label is uploading
  if (anyUploading && !uploading[key]) {
    alert('Another file is still uploading. Please wait until it finishes.');
    return;
  }

  setUploading((p) => ({ ...p, [key]: true }));
  setProgress((p) => ({ ...p, [key]: 0 }));

  try {
    // mirror to pending for UI consistency (not strictly required)
    setPendingFile(key, file);

    if (typeof autoUploadFile === 'function') {
      await autoUploadFile(key, file, (pct) => {
        setProgress((p) => ({ ...p, [key]: pct }));
      });
    } else {
      await doUpload(key, (pct) => {
        setProgress((p) => ({ ...p, [key]: pct }));
      }, file);
    }

    setPendingFile(key, null);
    setProgress((p) => ({ ...p, [key]: 100 }));

    // ✅ exit replace mode after a successful auto-upload
    setReplacing((p) => ({ ...p, [key]: false }));

    // optimistic "File uploaded" badge until parent state arrives
    setJustReplaced((p) => ({ ...p, [key]: true }));
  } catch (e) {
    // parent already alerted
  } finally {
    setTimeout(() => {
      setUploading((p) => ({ ...p, [key]: false }));
    }, 300);
  }
};


  // Patch a single writer row by index and write back the whole writers array
const patchWriterRow = (rowIdx, patch) => {
  const current = Array.isArray(writers) ? writers : [];
  const next = [...current];
  next[rowIdx] = { ...(next[rowIdx] || {}), ...patch };
  setField('writers', next);
};

  /* ─────────────────────────────────────────────────────────────
   * Defaults / one-time inits
   * ────────────────────────────────────────────────────────────*/
  const didVersionInitRef = useRef(false);
  useEffect(() => {
    if (didVersionInitRef.current) return;
    if (!st.versionType) setField('versionType', 'Original');
    didVersionInitRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefill contributors once from main artists (if empty)
  const didPrefillRef = useRef(false);
  useEffect(() => {
    if (didPrefillRef.current) return;
    if (!Array.isArray(mainArtists) || mainArtists.length === 0) return;
    if (Array.isArray(contributors) && contributors.length > 0) return;

    const prefill = mainArtists
      .filter(a => a && a.name)
      .map(a => ({
        name: a.name,
        splitPct: '',
        needSideAgreement: null,
        sideLegalName: '',
        sideEmail: '',
      }));

    if (prefill.length > 0) {
      setField('contributors', prefill);
      didPrefillRef.current = true;
    }
  }, [mainArtists, contributors, setField]);

  // Clear optimistic flags when the real uploads arrive from parent
useEffect(() => {
  const labels = Object.keys(justReplaced || {});
  if (!labels.length) return;
  labels.forEach((k) => {
    if (st?.uploads?.[k]) {
      setJustReplaced((p) => {
        const copy = { ...p };
        delete copy[k];
        return copy;
      });
    }
  });
}, [st?.uploads, justReplaced]);

  /* ─────────────────────────────────────────────────────────────
   * Main Artist add input (typeahead)
   * ────────────────────────────────────────────────────────────*/
  const [mainInput, setMainInput] = useState('');
  const [mainOpen,  setMainOpen]  = useState(false);
  const mainBoxRef = useRef(null);

  useEffect(() => {
    const clickAway = (e) => {
      if (mainBoxRef.current && !mainBoxRef.current.contains(e.target)) {
        setMainOpen(false);
      }
    };
    document.addEventListener('mousedown', clickAway);
    return () => document.removeEventListener('mousedown', clickAway);
  }, []);

  const norm = (s) => String(s || '').toLowerCase();
  
  const takenIdsMain = new Set(mainArtists.map((a) => a?._id).filter(Boolean));

  const filteredMain = useMemo(() => {
    const q = norm(mainInput);
    if (!q) return [];
    return allArtists.filter((a) => norm(a.name).includes(q) && !takenIdsMain.has(a._id));
  }, [mainInput, allArtists, takenIdsMain]);

  const setMainArtists  = (arr) => setField('mainArtists', arr);
  const setContributors = (arr) => setField('contributors', arr);

  const addMainArtist = (artistObj) => {
    setMainArtists([...(mainArtists || []), { ...artistObj, role: 'Primary' }]);

    // also add as contributor if not present
    if (!contributors.some((c) => c.name === artistObj.name)) {
      setContributors([
        ...contributors,
        {
          name: artistObj.name,
          splitPct: '',
          needSideAgreement: null,
          sideLegalName: '',
          sideEmail: '',
        },
      ]);
    }

    setMainInput('');
    setMainOpen(false);
  };

  const removeMainArtist = (idx) => {
    const arr = mainArtists.filter((_, i) => i !== idx);
    setMainArtists(arr);
  };

  const setMainArtistField = (idx, field, value) => {
    const arr = [...mainArtists];
    arr[idx] = { ...(arr[idx] || {}), [field]: value };
    setMainArtists(arr);
  };

  const createNewArtist = async (name, cb) => {
    try {
      const res = await api('/api/artists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error(`Failed to create artist (${res.status})`);
      const newArtist = await res.json();
      cb(newArtist);
    } catch (err) {
      alert(err.message);
    }
  };

  /* ─────────────────────────────────────────────────────────────
   * Contributors logic (reverted look/feel)
   * ────────────────────────────────────────────────────────────*/
  const updateContributor = (idx, field, value) => {
    const arr = [...contributors];
    arr[idx] = { ...(arr[idx] || {}), [field]: value };
    setContributors(arr);
  };

  const addContributor = () => {
    setContributors([
      ...contributors,
      {
        name: '',
        splitPct: '',
        needSideAgreement: null,
        sideLegalName: '',
        sideEmail: '',
      },
    ]);
  };

  const removeContributor = (idx) => {
    setContributors(contributors.filter((_, i) => i !== idx));
  };

  const totalSplit = useMemo(() =>
    contributors.reduce((sum, c) => sum + (Number(c?.splitPct) || 0), 0),
  [contributors]);
  const splitWarn = totalSplit !== 100;

  // total publishing split across writers
const writersTotalSplit = useMemo(
  () => writers.reduce((sum, w) => sum + (Number(w?.percentage) || 0), 0),
  [writers]
);
const writersSplitWarn = writersTotalSplit !== 100;

  const ArtistChip = ({ name }) => (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 10px',
        background: '#222',
        borderRadius: 999,
        color: '#fff',
        fontSize: 13,
      }}
    >
      <span>{name}</span>
    </div>
  );

  /* ─────────────────────────────────────────────────────────────
   * Writers: DB index + row helpers (typeahead + create)
   * ────────────────────────────────────────────────────────────*/
  const [allWriters, setAllWriters] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await api('/api/writers');
        if (!r.ok) throw new Error(`writers index failed (${r.status})`);
        setAllWriters(await r.json());
      } catch (e) {
        console.error('load writers failed', e);
        setAllWriters([]);
      }
    })();
  }, []);

  // per-row dropdown open + create form state
  const [writerOpen, setWriterOpen] = useState({}); // idx -> bool
  const [createRow, setCreateRow]   = useState({}); // idx -> {pro:'', ipi:'', publisher:''} | undefined

  const setWriterOpenRow  = (idx, val) => setWriterOpen((p) => ({ ...p, [idx]: val }));
  const setCreateRowField = (idx, field, val) =>
    setCreateRow((p) => ({ ...p, [idx]: { ...(p[idx] || { pro:'', ipi:'', publisher:'' }), [field]: val } }));



  // helper: exact-match a writer in DB by legal name
const findWriterByName = (name = '') => {
  const q = name.trim().toLowerCase();
  if (!q) return undefined;
  return allWriters.find((w) => (w.name || '').trim().toLowerCase() === q);
};

  const pickWriter = (rowIdx, wobj) => {
  patchWriterRow(rowIdx, {
    legalName: wobj?.name || '',
    pro:       wobj?.pro  || '',
    ipi:       wobj?.ipi  || '',
    // If CK publishing already chosen, keep it; otherwise use writer's publisher (if any)
    publisherName:
      writers[rowIdx]?.ckPublishing === true
        ? 'CloudKid'
        : (wobj?.publisher || (writers[rowIdx]?.publisherName || '')),
  });

  // close UI affordances
  setWriterOpenRow(rowIdx, false);
  setCreateRow((p) => ({ ...p, [rowIdx]: undefined }));
};

  const createAndPickWriter = async (rowIdx, legalName) => {
  const payload = {
    name:      (legalName || '').trim(),
    pro:       (createRow[rowIdx]?.pro || '').trim(),
    ipi:       (createRow[rowIdx]?.ipi || '').trim(),
    publisher: (createRow[rowIdx]?.publisher || '').trim(),
  };
  try {
    const r = await api('/api/writers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error(`Create writer failed (${r.status})`);
    const newW = await r.json();

    // Add to local index for future matches
    setAllWriters((prev) => [newW, ...prev]);

    // Fill the current writer row (single write)
    patchWriterRow(rowIdx, {
      legalName: newW?.name || payload.name,
      pro:       newW?.pro  || payload.pro,
      ipi:       newW?.ipi  || payload.ipi,
      publisherName:
        writers[rowIdx]?.ckPublishing === true
          ? 'CloudKid'
          : (newW?.publisher || payload.publisher || ''),
    });

    // Close dropdown and inline-create UI
    setWriterOpenRow(rowIdx, false);
    setCreateRow((p) => ({ ...p, [rowIdx]: undefined }));
  } catch (e) {
    alert(e.message || 'Failed to create writer');
  }
};

 

  const filteredWriterMatches = (query) => {
    const q = norm(query);
    if (!q) return [];
    return allWriters.filter(
      (w) => norm(w.name || '').includes(q)
    ).slice(0, 20);
  };

  /* ─────────────────────────────────────────────────────────────
   * RENDER
   * ────────────────────────────────────────────────────────────*/
  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #333' }}>
      {/* TWO-COLUMN GRID */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* LEFT COLUMN — Master – Track Info */}
        <div>
          <div style={{ fontWeight: 700, color: '#ddd', marginBottom: 8, fontSize: '1.2rem' }}>
  Master – Track Info
</div>

          {/* Release Title (+ Tracklist # when Album/EP) */}
          <Row label="Release Title">
  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
    <input
      value={st.title || ''}
      onChange={(e) => setField('title', e.target.value)}
      placeholder="Release title…"
      style={input.wide}
    />

    {showTracklist && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ display: 'flex', alignItems: 'center', color: '#bbb', fontSize: 12 }}>
          Tracklist #
          <InfoHelper text="Defines the track order of an album or EP on DSPs." />
        </span>
        <input
          type="number"
          min="1"
          value={st.tracklistNumber ?? ''}
          onChange={(e) => setField('tracklistNumber', e.target.value)}
          placeholder="#"
          style={{ ...input.sm, width: 90 }}
        />
      </div>
    )}
  </div>
</Row>



          {/* separator */}
          <div style={{ borderTop: '1px solid #333', margin: '12px 0' }} />

          {/* Release Version */}
          <Row label="Release Version">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {['Original', 'Remix', 'Alternative Version'].map((opt) => (
                <Chip
                  key={opt}
                  active={st.versionType === opt}
                  onClick={() => setField('versionType', opt)}
                  colorActive={opt === 'Remix' ? '#f3b40b' : opt === 'Alternative Version' ? '#555' : '#28a745'}
                >
                  {opt}
                </Chip>
              ))}

              {st.versionType === 'Remix' && (
                <div style={{ marginLeft: 8 }}>
                  <input
                    value={st.remixerName || ''}
                    onChange={(e) => setField('remixerName', e.target.value)}
                    placeholder="Remixer name…"
                    style={input.sm}
                  />
                </div>
              )}
              {st.versionType === 'Alternative Version' && (
                <div style={{ marginLeft: 8 }}>
                  <input
                    value={st.altVersionName || ''}
                    onChange={(e) => setField('altVersionName', e.target.value)}
                    placeholder="Version name (e.g., Dark Version)"
                    style={input.sm}
                  />
                </div>
              )}
            </div>
          </Row>

          {/* Re-Release extras */}
          {st.releaseType === 'Re-Release' && (
            <>
              <Row label="Original ISRC">
                <input
                  value={st.originalIsrc || ''}
                  onChange={(e) => setField('originalIsrc', e.target.value)}
                  placeholder="Original ISRC…"
                  style={input.sm}
                />
              </Row>
              <Row label="Original Release Date">
                <input
                  type="date"
                  value={st.originalReleaseDate || ''}
                  onChange={(e) => setField('originalReleaseDate', e.target.value)}
                  style={input.sm}
                />
              </Row>
            </>
          )}

          {/* separator */}
          <div style={{ borderTop: '1px solid #333', margin: '12px 0' }} />

          {/* Main Artists */}
          <Row label="Main Artist(s)">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: input.wide.width }}>
              {/* input + dropdown */}
              <div ref={mainBoxRef} style={{ position: 'relative' }}>
                <input
                  value={mainInput}
                  onChange={(e) => {
                    setMainInput(e.target.value);
                    setMainOpen(true);
                  }}
                  onFocus={() => setMainOpen(true)}
                  placeholder="Add main artist (type to search or create)…"
                  style={input.wide}
                />

                {mainOpen && mainInput && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 40,
                      left: 0,
                      width: input.wide.width,
                      background: '#1e1e1e',
                      border: '1px solid #333',
                      borderRadius: 6,
                      zIndex: 10,
                      maxHeight: 220,
                      overflowY: 'auto',
                    }}
                  >
                    {filteredMain.length ? (
                      filteredMain.map((cand) => (
                        <div
                          key={cand._id}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => addMainArtist(cand)}
                          style={{ padding: 8, cursor: 'pointer', borderBottom: '1px solid #2a2a2a' }}
                        >
                          {cand.name}
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: 8, color: '#888' }}>No matches</div>
                    )}
                    <div
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => createNewArtist(mainInput, (newA) => addMainArtist(newA))}
                      style={{ padding: 8, color: '#28a745', fontWeight: 600, cursor: 'pointer' }}
                    >
                      + Create “{mainInput}”
                    </div>
                  </div>
                )}
              </div>

              {/* listed artists (full width), with Role and remove */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: input.wide.width }}>
                {mainArtists.map((a, idx) => (
                  <div
                    key={`${a?._id || 'temp'}-${idx}`}
                    style={{
                      width: '100%',
                      display: 'grid',
                      gridTemplateColumns: '1fr auto auto',
                      gap: 12,
                      alignItems: 'center',
                      padding: 8,
                      background: '#111',
                      borderRadius: 8,
                    }}
                  >
                    <ArtistChip name={a?.name || ''} />

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: '#bbb', fontSize: 12 }}>Role:</span>
                      <Chip
  active={(a?.role || 'Primary') === 'Primary'}
  onClick={() => setMainArtistField(idx, 'role', 'Primary')}
  colorActive="#28a745" // keep green for Primary
>
  Primary
</Chip>
<Chip
  active={a?.role === 'Featuring'}
  onClick={() => setMainArtistField(idx, 'role', 'Featuring')}
  colorActive="#555555" // gray when Featuring is selected
>
  Featuring
</Chip>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => removeMainArtist(idx)}
                        style={{
                          background: '#333',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          padding: '6px 10px',
                          cursor: 'pointer',
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Row>

          {/* separator */}
          <div style={{ borderTop: '1px solid #333', margin: '12px 0' }} />

          {/* Contributors */}
<Row label="Master Splits">
  <div style={{ flex: 1 }}>
    {/* list */}
    {contributors.map((c, idx) => (
      <div
        key={idx}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: 8,
          background: '#111',
          borderRadius: 8,
          marginBottom: 8,
          width: input.wide.width,
        }}
      >
        {/* Top row: name + % + remove */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            value={c.name}
            onChange={(e) => updateContributor(idx, 'name', e.target.value)}
            placeholder="Contributor name"
            style={{ ...input.sm, flex: 1 }}
          />
          <input
            type="number"
            value={c.splitPct}
            onChange={(e) => updateContributor(idx, 'splitPct', e.target.value)}
            placeholder="Master %"
            style={{ ...input.sm, width: 70 }}
          />
          <span style={{ color: '#bbb' }}>%</span>
          <button
            onClick={() => removeContributor(idx)}
            style={{
              background: '#333',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '6px 10px',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>

        {/* Side Agreement toggle */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ color: '#bbb', fontSize: 12 }}>Do you need a Side Artist Agreement?</span>
          <Chip
            active={c.needSideAgreement === true}
            onClick={() => updateContributor(idx, 'needSideAgreement', true)}
          >
            Yes
          </Chip>
          <Chip
            active={c.needSideAgreement === false}
            onClick={() => updateContributor(idx, 'needSideAgreement', false)}
          >
            No
          </Chip>
        </div>

        {/* Legal Name + Email when Yes */}
        {c.needSideAgreement === true && (
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={c.sideLegalName || ''}
              onChange={(e) => updateContributor(idx, 'sideLegalName', e.target.value)}
              placeholder="Side Artist Legal Name"
              style={{ ...input.sm, flex: 1 }}
            />
            <input
              type="email"
              value={c.sideEmail || ''}
              onChange={(e) => updateContributor(idx, 'sideEmail', e.target.value)}
              placeholder="Email"
              style={{ ...input.sm, flex: 1 }}
            />
          </div>
        )}
      </div>
    ))}

    {/* total split helper */}
    <div style={{ color: splitWarn ? '#ffb347' : '#8bc34a', fontSize: 12, marginTop: 4 }}>
      Total split: {Number.isFinite(totalSplit) ? totalSplit : 0}%{splitWarn && ' (Should be 100%)'}
    </div>

    {/* moved to bottom & styled green like "+ Add writer" */}
    <button
      onClick={addContributor}
      style={{
        padding: '8px 12px',
        background: '#28a745',
        color: '#fff',
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
        fontWeight: 700,
        marginTop: 10,
      }}
    >
      + Add contributor
    </button>
  </div>
</Row>

          

          {/* separator */}
          <div style={{ borderTop: '1px solid #333', margin: '12px 0' }} />

          {/* Credits / core info (vocals/explicit/credits…) */}
          <Row label="Are there vocals?">
            <div style={{ display: 'flex', gap: 8 }}>
              <Chip active={st.hasVocals === true} onClick={() => setField('hasVocals', true)}>Yes</Chip>
              <Chip active={st.hasVocals === false} onClick={() => setField('hasVocals', false)}>No</Chip>
            </div>
          </Row>

          <Row label="Is explicit?">
            <div style={{ display: 'flex', gap: 8 }}>
              <Chip active={st.isExplicit === true} onClick={() => setField('isExplicit', true)}>Yes</Chip>
              <Chip active={st.isExplicit === false} onClick={() => setField('isExplicit', false)}>No</Chip>
            </div>
          </Row>

          <Row label="Vocalist(s)">
            <input
              value={st.vocalists || ''}
              onChange={(e) => setField('vocalists', e.target.value)}
              placeholder="Names…"
              style={input.wide}
            />
          </Row>

          <Row label="Producer">
            <input
              value={st.producer || ''}
              onChange={(e) => setField('producer', e.target.value)}
              placeholder="Name…"
              style={input.wide}
            />
          </Row>

          <Row label="Mixing Engineer">
            <input
              value={st.mixer || ''}
              onChange={(e) => setField('mixer', e.target.value)}
              placeholder="Name…"
              style={input.wide}
            />
          </Row>

          <Row label="Mastering Engineer">
            <input
              value={st.masteringEngineer || ''}
              onChange={(e) => setField('masteringEngineer', e.target.value)}
              placeholder="Name…"
              style={input.wide}
            />
          </Row>

          <Row
  label={
    <span style={{ display: 'flex', alignItems: 'center' }}>
      Instruments & performers
      <InfoHelper text="What instruments are being played and by who, write the core instrumentation even if instruments are virtual, plugins, etc.?" />
    </span>
  }
>
  <textarea
    value={st.instrumentsAndPerformers || ''}
    onChange={(e) => setField('instrumentsAndPerformers', e.target.value)}
    placeholder="e.g., Jane Doe  – Vocals; John Doe – Synths; …"
    rows={4}
    style={{ ...input.wide, resize: 'vertical' }}
  />
</Row>

          <Row label="Recording country">
            <input
              value={st.recordingCountry || ''}
              onChange={(e) => setField('recordingCountry', e.target.value)}
              placeholder="e.g., Germany"
              style={input.wide}
            />
          </Row>

          <Row label="TikTok Snippet (start time)">
            <input
              value={st.tiktokSnippet || ''}
              onChange={(e) => setField('tiktokSnippet', e.target.value)}
              placeholder="e.g., 1:02"
              style={input.wide}
            />
          </Row>

          <Row label="Notes">
            <textarea
              value={st.notes || ''}
              onChange={(e) => setField('notes', e.target.value)}
              rows={3}
              placeholder="Any important notes…"
              style={{ ...input.wide, resize: 'vertical' }}
            />
          </Row>
        </div>

        {/* RIGHT COLUMN — Publishing – Writers + Uploads */}
        <div>
          <div style={{ fontWeight: 700, color: '#ddd', marginBottom: 8, fontSize: '1.2rem' }}>
  Publishing – Writers
</div>

          {writers.map((w, i) => {
            const matches = filteredWriterMatches(w.legalName || '');
            const open    = !!writerOpen[i];
            const create  = createRow[i];

            return (
              <div key={i} style={{ padding: 10, border: 'none', borderRadius: 8, marginBottom: 8, background: '#111' }}>
                {/* WRITER NAME + typeahead */}
<Row label="Writer’s legal name" compact>
  <div style={{ position: 'relative', width: input.sm.width }}>
    <input
      value={w.legalName || ''}
      onChange={(e) => {
        onWriterChange(i, 'legalName', e.target.value);
        setWriterOpenRow(i, true);
      }}
      onFocus={() => setWriterOpenRow(i, true)}
      placeholder="Legal Name (Type to search or create)…"
      style={input.sm}
    />

    {writerOpen[i] && (w.legalName || '').trim() && (
      <div
        style={{
          position: 'absolute',
          top: 38,
          left: 0,
          width: input.sm.width,
          background: '#1e1e1e',
          border: '1px solid #333',
          borderRadius: 6,
          zIndex: 10,
          maxHeight: 220,
          overflowY: 'auto',
        }}
      >
        {/* matches list */}
        {filteredWriterMatches(w.legalName || '').length ? (
          filteredWriterMatches(w.legalName || '').map((mw) => (
            <div
              key={mw._id}
              onMouseDown={(e) => {
                e.preventDefault();
                pickWriter(i, mw);
                setWriterOpenRow(i, false);
                setCreateRow((p) => ({ ...p, [i]: undefined }));
              }}
              style={{ padding: 8, cursor:'pointer', borderBottom:'1px solid #2a2a2a' }}
            >
              {mw.name}
            </div>
          ))
        ) : (
          <div style={{ padding: 8, color:'#888' }}>No matches</div>
        )}

        {/* open inline create form */}
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            setCreateRowField(i, 'open', true);
            setWriterOpenRow(i, false);
          }}
          style={{ padding: 8, color:'#28a745', fontWeight:600, cursor:'pointer' }}
        >
          + Create “{w.legalName || ''}”
        </div>
      </div>
    )}
  </div>
</Row>

{/* Inline create form for new writer */}
{createRow[i]?.open && (
  <div style={{ marginLeft: 160, marginBottom: 8 }}>
    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
      <input
        value={createRow[i].pro || ''}
        onChange={(e)=>setCreateRowField(i,'pro',e.target.value)}
        placeholder="PRO"
        style={input.sm}
      />
      <input
        value={createRow[i].ipi || ''}
        onChange={(e)=>setCreateRowField(i,'ipi',e.target.value)}
        placeholder="IPI"
        style={input.sm}
      />
      <input
        value={createRow[i].publisher || ''}
        onChange={(e)=>setCreateRowField(i,'publisher',e.target.value)}
        placeholder="Publisher"
        style={input.sm}
      />
      <button
        onClick={() => createAndPickWriter(i, w.legalName || '')}
        style={{
          padding:'6px 10px', background:'#28a745', color:'#fff',
          border:'none', borderRadius:6, cursor:'pointer', fontWeight:700,
        }}
      >
        Create new Writer and Fill
      </button>
      <button
        onClick={() => setCreateRow((p)=>({ ...p, [i]: undefined }))}
        style={{
          padding:'6px 10px', background:'#333', color:'#fff',
          border:'none', borderRadius:6, cursor:'pointer',
        }}
      >
        Cancel
      </button>
    </div>
  </div>
)}

                {/* PRO / IPI */}
                <Row label="PRO" compact>
  <input
    value={w.pro || ''}
    onChange={(e) => onWriterChange(i, 'pro', e.target.value)}
    placeholder="e.g., ASCAP, GEMA…"
    style={input.sm}
  />
</Row>

<Row label="IPI Number" compact>
  <input
    value={w.ipi || ''}
    onChange={(e) => onWriterChange(i, 'ipi', e.target.value)}
    placeholder="e.g., 123456789"
    style={input.sm}
  />
</Row>

                <Row label="Does CloudKid have Publishing?" compact>
  <div style={{ display: 'flex', gap: 8 }}>
    <Chip
      active={!!w.ckPublishing}
      onClick={() => {
        // CK = Yes → force publisher CloudKid; clear selfPublished
        patchWriterRow(i, {
          ckPublishing: true,
          selfPublished: undefined,
          publisherName: 'CloudKid',
        });
      }}
    >
      Yes
    </Chip>
    <Chip
      active={w.ckPublishing === false}
      onClick={() => {
        // CK = No → unset publisher until Self Published is chosen (or writer pick fills it)
        patchWriterRow(i, {
          ckPublishing: false,
          selfPublished: undefined,
          publisherName:
            w.publisherName && w.publisherName !== 'CloudKid' ? w.publisherName : '',
        });
      }}
    >
      No
    </Chip>
  </div>
</Row>

                {/* Publisher logic */}
                {w.ckPublishing === true && (
                  <Row label="Publisher Name" compact>
  <input
    value={w.publisherName || ''}
    onChange={(e) => onWriterChange(i, 'publisherName', e.target.value)}
    placeholder="e.g., Kobalt"
    style={input.sm}
  />
</Row>
                )}

                {w.ckPublishing === false && (
                  <>
                    <Row label="Self Published?" compact>
  <div style={{ display: 'flex', gap: 8 }}>
    <Chip
      active={w.selfPublished === true}
      onClick={() =>
        patchWriterRow(i, {
          selfPublished: true,
          publisherName: 'Copyright Control',
        })
      }
    >
      Yes
    </Chip>
    <Chip
  active={w.selfPublished === false}
  onClick={() => {
    // look up a writer by the current legalName and take their stored publisher if any
    const ref = findWriterByName(w.legalName || '');
    const dbPublisher = (ref?.publisher || '').trim();

    patchWriterRow(i, {
      selfPublished: false,
      // if DB has a publisher, use it; if the row already has a non-CC publisher, keep it; else blank
      publisherName:
        dbPublisher ||
        (w.publisherName && w.publisherName !== 'Copyright Control' ? w.publisherName : ''),
    });
  }}
>
  No
</Chip>
  </div>
</Row>

                    {/* If self-published → auto Copyright Control; else editable */}
                    {w.selfPublished === true ? (
                      <Row label="Publisher Name" compact>
                        <input value="Copyright Control" readOnly style={{ ...input.sm, opacity: 0.7 }} />
                      </Row>
                    ) : w.selfPublished === false ? (
                      <Row label="Publisher Name" compact>
                        <input
                          value={w.publisherName || ''}
                          onChange={(e) => onWriterChange(i, 'publisherName', e.target.value)}
                          style={input.sm}
                        />
                      </Row>
                    ) : null}
                  </>
                )}

                {/* Percentage LAST */}
                <Row label="Pub Percentage" compact>
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <input
      type="number"
      value={w.percentage || ''}
      onChange={(e) => onWriterChange(i, 'percentage', e.target.value)}
      placeholder="0"
      style={{ ...input.sm, width: 90 }}
    />
    <span style={{ color: '#bbb' }}>%</span>
  </div>
</Row>

{/* NEW: Involvement (e.g., Vocalist, Composer) */}
<Row label="Involvement" compact>
  <input
    value={w.involvement || ''}
    onChange={(e) => onWriterChange(i, 'involvement', e.target.value)}
    placeholder="e.g., Composer, Vocalist"
    style={input.sm}
  />
</Row>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => removeWriter(i)}
                    style={{
                      padding: '6px 10px',
                      background: '#333',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                    }}
                  >
                    Remove writer
                  </button>
                </div>
              </div>
            );
          })}

<div style={{ color: writersSplitWarn ? '#ffb347' : '#8bc34a', fontSize: 12, marginBottom: 8 }}>
  Total Split: {Number.isFinite(writersTotalSplit) ? writersTotalSplit : 0}% {writersSplitWarn && '(should be 100%)'}
</div>

          <button
            onClick={addWriter}
            style={{
              padding: '8px 12px',
              background: '#28a745',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              marginBottom: 16,
              fontWeight: 700,
            }}
          
          >
            + Add writer
          </button>


{/* Uploads */}
<div style={{ fontWeight: 700, color: '#ddd', margin: '8px 0' }}>Uploads</div>

{[
  ['lyricsPdf',             'Lyrics PDF'             ],
  ['losslessMaster',        'Lossless Master'        ],
  ['mp3Master',             'MP3 Master'             ],
  ['losslessInstrumental',  'Lossless Instrumental'  ],
  ['losslessCleanMaster',   'Lossless Clean Master'  ],
  ['acapella',              'Acapella'               ],
  ['artwork',               'Artwork'                ],
  ['artworkPsd',            'Artwork PSD'            ],
  ['pressPics',             'Press Pictures'         ],
].map(([key, label]) => {
  const accept =
    key === 'lyricsPdf'            ? 'application/pdf'
  : key === 'pressPics'            ? '.zip,application/zip,application/x-zip-compressed,application/x-zip'
  : key === 'artwork'              ? 'image/jpeg,image/jpg,image/png'
  : key === 'artworkPsd'           ? '.psd,.psb,application/vnd.adobe.photoshop,.zip,application/zip,application/x-zip-compressed'
  : key === 'mp3Master'            ? 'audio/mpeg'
  :                                   'audio/*';

  return (
    <Row
      key={key}
      label={
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <span>{label}</span>
          {UPLOAD_HELPERS[key] && <InfoHelper text={UPLOAD_HELPERS[key]} />}
        </span>
      }
      compact
    >
      {(() => {
        const hasServerFile = !!st.uploads?.[key];
const justUploaded  = (progress[key] === 100) || !!justReplaced[key];
const hasFile       = hasServerFile || justUploaded;

const isReplacing = !!replacing[key];
const isUploading = !!uploading[key];
const disableThisPicker = anyUploading && !isUploading;

        return (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>

{/* LEFT: fixed-width wrapper; always show input when not locked */}
<div style={{ width: input.sm.width }}>
  {locked ? (
    hasFile ? (
      <span
        style={{
          display: 'inline-block',
          width: '100%',
          color: '#8bc34a',
          background: '#1f2a1f',
          border: 'none',
          padding: 6,
          borderRadius: 6,
          fontSize: 12,
          textAlign: 'center',
          boxSizing: 'border-box',
        }}
      >
        File uploaded
      </span>
    ) : (
      <span
        style={{
          display: 'inline-block',
          width: '100%',
          color: '#bbb',
          background: '#1f1f1f',
          border: 'none',
          padding: 6,
          borderRadius: 6,
          fontSize: 12,
          textAlign: 'center',
          boxSizing: 'border-box',
        }}
      >
        No file
      </span>
    )
  ) : hasFile && !isReplacing ? (
    // ✅ show the green badge after upload (until user hits "Replace")
    <span
      style={{
        display: 'inline-block',
        width: '100%',
        color: '#8bc34a',
        background: '#1f2a1f',
        border: 'none',
        padding: 6,
        borderRadius: 6,
        fontSize: 12,
        textAlign: 'center',
        boxSizing: 'border-box',
      }}
    >
      File uploaded
    </span>
  ) : (
    // picker (new upload or replace mode)
    <input
  type="file"
  accept={accept}
  onChange={(e) => {
    const f = e.target.files?.[0] || null;
    if (f) autoUpload(key, f);
  }}
  disabled={isUploading || disableThisPicker}
  style={{
    ...input.sm,
    width: '100%',
    opacity: (isUploading || disableThisPicker) ? 0.7 : 1,
    boxSizing: 'border-box',
  }}
/>
  )}
</div>
              

{/* PRIMARY BUTTON: Replace / Upload / Uploading % */}
{!locked && (
  <button
    onClick={async () => {
      if (disableThisPicker) {
        alert('Another file is still uploading. Please wait until it finishes.');
        return;
      }

      // If we already have a file and we're *not* in replace mode and *not* uploading,
      // entering replace mode simply reveals the file input + Cancel.
      if (hasFile && !isReplacing && !isUploading) {
        setReplacing((p) => ({ ...p, [key]: true }));
        setProgress((p) => ({ ...p, [key]: 0 }));
        return;
      }

      // Otherwise: do the upload (new or replace)
      const file = (st.pendingFiles || {})[key];
      if (!file) {
        alert('Choose a file first.');
        return;
      }

      setUploading((p) => ({ ...p, [key]: true }));
      setProgress((p) => ({ ...p, [key]: 0 }));

      try {
        await doUpload(key, (pct) => {
          setProgress((p) => ({ ...p, [key]: pct }));
        });

        setProgress((p) => ({ ...p, [key]: 100 }));
        setPendingFile(key, null);

        // ✅ immediately exit replace mode and show the green badge
        setReplacing((p) => ({ ...p, [key]: false }));
        setJustReplaced((p) => ({ ...p, [key]: true }));
      } catch (_) {
        // errors are already alerted upstream
      } finally {
        setTimeout(() => {
          setUploading((p) => ({ ...p, [key]: false }));
        }, 300);
      }
    }}
    disabled={isUploading || disableThisPicker}
    style={{
      padding: '6px 10px',
      background: (isUploading || disableThisPicker) ? '#555' : '#333',
      color: '#fff',
      border: 'none',
      borderRadius: 6,
      cursor: (isUploading || disableThisPicker) ? 'not-allowed' : 'pointer',
      minWidth: 110,
    }}
  >
    {isUploading
      ? `Uploading ${Math.max(0, Math.min(100, progress[key] ?? 0))}%`
      : (hasFile && !isReplacing ? 'Replace' : 'Upload')}
  </button>
)}

              {/* CANCEL only when replacing and not uploading */}
              {hasFile && isReplacing && !isUploading && (
                <button
                  type="button"
                  onClick={() => {
                    setReplacing((p) => ({ ...p, [key]: false }));
                    setPendingFile(key, null);
                  }}
                  style={{
                    padding: '6px 10px',
                    background: '#555',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              )}

              {/* VIEW ICON */}
{hasFile && (
  /^https?:\/\//i.test(st.uploads[key]) ? (
    <a
      href={st.uploads[key]}
      target="_blank"
      rel="noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        marginLeft: 8,
        marginRight: 4,
        borderRadius: 6,
        background: '#222',
        border: '1px solid #2f2f2f',
        cursor: 'pointer',
      }}
      title="View file"
    >
      <ViewIcon style={{ width: 16, height: 16, fill: '#aaa' }} />
    </a>
  ) : (
    <button
      type="button"
      onClick={() => onViewFile && onViewFile(key)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        marginLeft: 8,
        marginRight: 4,
        borderRadius: 6,
        background: '#222',
        border: '1px solid #2f2f2f',
        cursor: 'pointer',
      }}
      title="View file"
    >
      <ViewIcon style={{ width: 16, height: 16, fill: '#aaa' }} />
    </button>
  )
)}

{/* DOWNLOAD ICON */}
{hasFile && (
  <button
    type="button"
    onClick={() => onDownloadFile && onDownloadFile(key)}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      marginLeft: 4,
      borderRadius: 6,
      background: '#222',
      border: '1px solid #2f2f2f',
      cursor: 'pointer',
    }}
    title="Download file"
  >
    <DownloadIcon style={{ width: 16, height: 16, fill: '#aaa' }} />
  </button>
)}
            </div>

            

            {/* progress bar */}
            {isUploading && (
              <div style={{ marginTop: 6, width: input.sm.width }}>
                <div style={{ height: 6, background: '#222', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${Math.max(0, Math.min(100, progress[key] ?? 0))}%`,
                    background: '#28a745',
                    transition: 'width .2s linear'
                  }} />

                  
                </div>
              </div>
            )}
          </>
        );
        
      })()}
    </Row>
  );

  
})}

        </div>
      </div>

            {/* Save + Save & Submit */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          marginTop: 16,
          borderTop: '1px solid #333',
          paddingTop: 12,
          flexWrap: 'wrap',
        }}
      >
        {/* Save (disabled when locked) */}
        <button
          onClick={onSave}
          disabled={locked}
          style={{
            padding: '10px 16px',
            background: locked ? '#555' : '#28a745',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            cursor: locked ? 'not-allowed' : 'pointer',
            fontWeight: 700,
            opacity: locked ? 0.7 : 1,
          }}
          title={locked ? 'This release is locked after submission' : 'Save'}
        >
          Save
        </button>

        {/* Save & Submit */}
<button
  onClick={handleSaveAndSubmit}   // 👈 use our combined handler
  disabled={locked || localSubmitting}
  style={{
    padding: '10px 16px',
    background: locked || localSubmitting ? '#555' : '#333',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    cursor: locked || localSubmitting ? 'not-allowed' : 'pointer',
    fontWeight: 700,
  }}
  title={
    locked
      ? 'This release is already submitted and locked'
      : 'Save & Submit assets'
  }
>
  {localSubmitting ? 'Submitting…' : (locked ? 'Submitted' : 'Save & Submit')}
</button>
      </div>
    </div>
  );
}